import React, { useState, useEffect, useRef } from 'react';

const S3_URL =
	'https://portfolio-projects-2024.s3.us-west-1.amazonaws.com/public/tulsa-streets.json';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const MAP_CENTER = [36.164, -95.99]; // Greenwood District
const MAP_ZOOM = 14;

function riskLevel(count, max) {
	const t = count / max;
	if (t > 0.66) return 'high';
	if (t > 0.33) return 'medium';
	return 'low';
}

function riskColor(level) {
	if (level === 'high') return { fill: '#E07070', glow: '#E0707055' };
	if (level === 'medium') return { fill: '#C88A50', glow: '#C88A5055' };
	return { fill: '#2BA898', glow: '#2BA89855' };
}

function useLeaflet() {
	const [ready, setReady] = useState(!!window.L);
	useEffect(() => {
		if (window.L) {
			setReady(true);
			return;
		}
		if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = LEAFLET_CSS;
			document.head.appendChild(link);
		}
		const script = document.createElement('script');
		script.src = LEAFLET_JS;
		script.onload = () => setReady(true);
		document.head.appendChild(script);
	}, []);
	return ready;
}

export default function TulsaHeatmap({ C, useBreakpoint }) {
	const leafletReady = useLeaflet();
	const mapRef = useRef(null);
	const mapInstanceRef = useRef(null);
	const markersRef = useRef(null);

	const [streets, setStreets] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selected, setSelected] = useState(null);
	const [filter, setFilter] = useState('all');
	const [listOpen, setListOpen] = useState(true);

	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	useEffect(() => {
		if (isMobile) setListOpen(false);
	}, [isMobile]);

	useEffect(() => {
		fetch(S3_URL)
			.then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.json();
			})
			.then((raw) => {
				const arr = Array.isArray(raw)
					? raw
					: (raw.streets ?? Object.values(raw));
				const cleaned = arr
					.filter((d) => d.lat && d.lng && d.count)
					.sort((a, b) => b.count - a.count);
				setStreets(cleaned);
				setLoading(false);
			})
			.catch((err) => {
				setError('Could not load street data. Check S3 URL and CORS settings.');
				setLoading(false);
			});
	}, []);

	useEffect(() => {
		if (!leafletReady || !mapRef.current || mapInstanceRef.current) return;
		const L = window.L;

		const map = L.map(mapRef.current, {
			center: MAP_CENTER,
			zoom: MAP_ZOOM,
			zoomControl: false,
			attributionControl: false,
		});

		L.tileLayer(
			'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
			{
				maxZoom: 19,
			},
		).addTo(map);

		L.control.zoom({ position: 'bottomright' }).addTo(map);
		L.control
			.attribution({ position: 'bottomleft', prefix: false })
			.addAttribution('© CartoDB · CDM 2025')
			.addTo(map);

		markersRef.current = L.layerGroup().addTo(map);
		mapInstanceRef.current = map;

		return () => {
			map.remove();
			mapInstanceRef.current = null;
		};
	}, [leafletReady]);

	useEffect(() => {
		if (!mapInstanceRef.current || !streets.length) return;
		const L = window.L;
		const max = streets[0].count;
		markersRef.current.clearLayers();

		const visible =
			filter === 'all'
				? streets
				: streets.filter((s) => riskLevel(s.count, max) === filter);

		visible.forEach((street) => {
			const level = riskLevel(street.count, max);
			const { fill, glow } = riskColor(level);
			const r = Math.max(8, Math.min(20, 8 + (street.count / max) * 12));

			const icon = L.divIcon({
				className: '',
				html: `<div style="
          width:${r * 2}px;height:${r * 2}px;border-radius:50%;
          background:${fill};opacity:0.88;
          box-shadow:0 0 ${r * 2}px ${glow},0 0 ${r}px ${fill}80;
          border:1.5px solid ${fill};
          display:flex;align-items:center;justify-content:center;
          font-family:'DM Mono',monospace;font-size:${r < 12 ? 9 : 11}px;
          color:white;font-weight:500;cursor:pointer;
        ">${street.count}</div>`,
				iconSize: [r * 2, r * 2],
				iconAnchor: [r, r],
			});

			const m = L.marker([street.lat, street.lng], { icon });
			m.on('click', () => {
				setSelected(street);
				if (isMobile) setListOpen(true);
			});
			markersRef.current.addLayer(m);
		});

		L.polygon(
			[
				[36.158, -96.002],
				[36.158, -95.983],
				[36.172, -95.983],
				[36.172, -96.002],
			],
			{
				color: '#2BA898',
				weight: 1.5,
				opacity: 0.4,
				fillColor: '#1A7A6E',
				fillOpacity: 0.04,
				dashArray: '6 4',
			},
		)
			.bindTooltip('Greenwood District', {
				permanent: false,
				direction: 'top',
				className: 'cdm-tip',
			})
			.addTo(markersRef.current);
	}, [streets, filter, leafletReady]);
	const max = streets.length ? streets[0].count : 1;
	const total = streets.reduce((s, d) => s + d.count, 0);
	const filtered =
		filter === 'all'
			? streets
			: streets.filter((s) => riskLevel(s.count, max) === filter);
	const S = {
		root: {
			display: 'flex',
			flexDirection: 'column',
			height: '100vh',
			background: '#0A1210',
			fontFamily: "'DM Sans', sans-serif",
			paddingTop: 100,
			zIndex: -1,
		},
		topBar: {
			flexShrink: 0,
			background: '#111E1C',
			borderBottom: '1px solid #1A2E2A',
			display: 'flex',
			alignItems: 'center',
			flexWrap: isMobile ? 'wrap' : 'nowrap',
			padding: isMobile ? '10px 16px' : `0 20px`,
			gap: isMobile ? 8 : 16,
			minHeight: isMobile ? 'auto' : 52,
		},
		body: {
			flex: 1,
			display: 'flex',
			overflow: 'hidden',
			position: 'relative',
		},
		mapWrap: {
			flex: 1,
			position: 'relative',
			display: isMobile && listOpen ? 'none' : 'block',
			zIndex: 1,
		},
		sidebar: {
			width: isMobile ? '100%' : 300,
			display: isMobile && !listOpen ? 'none' : 'flex',
			flexDirection: 'column',
			background: '#111E1C',
			borderLeft: '1px solid #1A2E2A',
			flexShrink: 0,
		},
	};

	const filterLabels = ['all', 'high', 'medium', 'low'];

	return (
		<div style={S.root}>
			<style>{`
        .cdm-tip {
          background:#111E1C!important; border:1px solid #1A2E2A!important;
          color:#2BA898!important; font-family:'DM Mono',monospace!important;
          font-size:10px!important; letter-spacing:.1em!important;
          text-transform:uppercase!important; box-shadow:none!important;
        }
        .cdm-tip::before { display:none!important; }
        .leaflet-control-zoom a {
          background:#111E1C!important; border:1px solid #1A2E2A!important;
          color:#2BA898!important; font-size:16px!important;
        }
        .leaflet-control-zoom a:hover { background:#1A7A6E!important; color:white!important; }
        .leaflet-control-attribution {
          background:rgba(10,18,16,.85)!important; color:#2A4A46!important;
          font-family:'DM Mono',monospace!important; font-size:9px!important;
        }
        .leaflet-control-attribution a { color:#4A6A66!important; }
        .cdm-row { padding:${isMobile ? '14px 16px' : '10px 14px'}; border-bottom:1px solid #1A2E2A; display:flex; align-items:center; gap:10px; cursor:pointer; transition:background .15s; }
        .cdm-row:hover { background:#162220; }
        .cdm-row.sel  { background:#162220; border-left:2px solid #2BA898; padding-left:12px; }
        .cdm-sb::-webkit-scrollbar { width:3px; }
        .cdm-sb::-webkit-scrollbar-track { background:transparent; }
        .cdm-sb::-webkit-scrollbar-thumb { background:#1A2E2A; border-radius:2px; }
        @keyframes cdm-spin { to { transform:rotate(360deg); } }
        @keyframes cdm-in   { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
        .cdm-in { animation: cdm-in .25s ease both; }
      `}</style>
			<div style={S.topBar}>
				<span
					style={{
						fontFamily: 'DM Mono',
						fontSize: isMobile ? 11 : 10,
						letterSpacing: '.12em',
						textTransform: 'uppercase',
						color: '#2BA898',
						whiteSpace: 'nowrap',
					}}
				>
					Greenwood · 1921
				</span>
				{!isMobile && (
					<div style={{ width: 1, height: 18, background: '#1A2E2A' }} />
				)}
				{!isMobile && (
					<span
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							color: '#4A6A66',
							whiteSpace: 'nowrap',
						}}
					>
						{loading ? '—' : `${streets.length} streets · ${total} disruptions`}
					</span>
				)}
				{isMobile && (
					<button
						onClick={() => setListOpen((o) => !o)}
						style={{
							marginLeft: 'auto',
							fontFamily: 'DM Mono',
							fontSize: 11,
							letterSpacing: '.1em',
							textTransform: 'uppercase',
							padding: '8px 16px',
							border: '1px solid #1A2E2A',
							cursor: 'pointer',
							background: listOpen ? '#1A7A6E' : 'transparent',
							color: listOpen ? 'white' : '#4A6A66',
							minHeight: 36,
						}}
					>
						{listOpen ? '← Map' : 'List →'}
					</button>
				)}
				<div
					style={{
						display: 'flex',
						gap: isMobile ? 4 : 3,
						marginLeft: isMobile ? 0 : 'auto',
						width: isMobile ? '100%' : 'auto',
					}}
				>
					{filterLabels.map((f) => (
						<button
							key={f}
							onClick={() => setFilter(f)}
							style={{
								fontFamily: 'DM Mono',
								fontSize: isMobile ? 11 : 9,
								letterSpacing: '.1em',
								textTransform: 'uppercase',
								padding: isMobile ? '9px 0' : '5px 10px',
								flex: isMobile ? 1 : 'none',
								border: '1px solid #1A2E2A',
								cursor: 'pointer',
								transition: 'all .15s',
								background: filter === f ? '#1A7A6E' : 'transparent',
								color: filter === f ? 'white' : '#4A6A66',
								minHeight: isMobile ? 38 : 'auto',
							}}
						>
							{f}
						</button>
					))}
				</div>
			</div>
			<div style={S.body}>
				<div ref={mapRef} style={S.mapWrap}>
					{(loading || !leafletReady) && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								zIndex: 500,
								background: '#0A1210',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								gap: 16,
							}}
						>
							<div
								style={{
									width: 28,
									height: 28,
									border: '2px solid #2BA898',
									borderTopColor: 'transparent',
									borderRadius: '50%',
									animation: 'cdm-spin .8s linear infinite',
								}}
							/>
							<span
								style={{
									fontFamily: 'DM Mono',
									fontSize: 10,
									color: '#4A6A66',
									letterSpacing: '.14em',
								}}
							>
								{!leafletReady ? 'LOADING MAP' : 'FETCHING DATA'}
							</span>
						</div>
					)}

					{error && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								zIndex: 500,
								background: '#0A1210',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 32,
							}}
						>
							<div
								style={{
									background: '#111E1C',
									border: '1px solid #1A2E2A',
									borderLeft: '3px solid #E07070',
									padding: '24px 28px',
									maxWidth: 380,
								}}
							>
								<div
									style={{
										fontFamily: 'DM Mono',
										fontSize: 10,
										color: '#E07070',
										letterSpacing: '.12em',
										textTransform: 'uppercase',
										marginBottom: 10,
									}}
								>
									Data Error
								</div>
								<p
									style={{
										fontSize: 14,
										color: '#6A8A86',
										lineHeight: 1.8,
										margin: 0,
									}}
								>
									{error}
								</p>
							</div>
						</div>
					)}

					{selected && !isMobile && (
						<div
							className='cdm-in'
							style={{
								position: 'absolute',
								bottom: 24,
								left: 24,
								zIndex: 1000,
								background: 'rgba(10,18,16,.95)',
								backdropFilter: 'blur(12px)',
								border: '1px solid #1A2E2A',
								borderLeft: '3px solid #2BA898',
								padding: '16px 20px',
								width: 300,
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 9,
									color: '#2BA898',
									letterSpacing: '.14em',
									textTransform: 'uppercase',
									marginBottom: 8,
								}}
							>
								Selected
							</div>
							<div
								style={{
									fontFamily: 'Playfair Display',
									fontSize: 14,
									color: 'white',
									lineHeight: 1.45,
									marginBottom: 12,
								}}
							>
								{selected.street}
							</div>
							<div style={{ display: 'flex', gap: 24, alignItems: 'flex-end' }}>
								<div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 28,
											color: '#2BA898',
											lineHeight: 1,
										}}
									>
										{selected.count}
									</div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											color: '#4A6A66',
											textTransform: 'uppercase',
											letterSpacing: '.08em',
											marginTop: 4,
										}}
									>
										disruptions
									</div>
								</div>
								<div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 10,
											letterSpacing: '.1em',
											textTransform: 'uppercase',
											padding: '3px 8px',
											border: '1px solid',
											...(riskLevel(selected.count, max) === 'high'
												? {
														color: '#E07070',
														borderColor: '#E0707040',
														background: '#2A181880',
													}
												: riskLevel(selected.count, max) === 'medium'
													? {
															color: '#C88A50',
															borderColor: '#C88A5040',
															background: '#221A1080',
														}
													: {
															color: '#2BA898',
															borderColor: '#2BA89840',
															background: '#0E1F1880',
														}),
										}}
									>
										{riskLevel(selected.count, max)}
									</div>
								</div>
							</div>
							<button
								onClick={() => setSelected(null)}
								style={{
									position: 'absolute',
									top: 10,
									right: 12,
									background: 'none',
									border: 'none',
									color: '#4A6A66',
									fontSize: 18,
									cursor: 'pointer',
									lineHeight: 1,
								}}
							>
								×
							</button>
						</div>
					)}
					{!isMobile && (
						<div
							style={{
								position: 'absolute',
								bottom: 80,
								right: 12,
								zIndex: 1000,
								background: 'rgba(10,18,16,.9)',
								border: '1px solid #1A2E2A',
								padding: '12px 14px',
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 9,
									color: '#4A6A66',
									letterSpacing: '.12em',
									textTransform: 'uppercase',
									marginBottom: 10,
								}}
							>
								Risk Level
							</div>
							{[
								['high', '#E07070'],
								['medium', '#C88A50'],
								['low', '#2BA898'],
							].map(([l, c]) => (
								<div
									key={l}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 8,
										marginBottom: 6,
									}}
								>
									<div
										style={{
											width: 9,
											height: 9,
											borderRadius: '50%',
											background: c,
											boxShadow: `0 0 5px ${c}80`,
										}}
									/>
									<span
										style={{
											fontFamily: 'DM Mono',
											fontSize: 10,
											color: '#6A8A86',
											textTransform: 'capitalize',
										}}
									>
										{l}
									</span>
								</div>
							))}
							<div
								style={{
									marginTop: 10,
									paddingTop: 10,
									borderTop: '1px solid #1A2E2A',
									display: 'flex',
									alignItems: 'center',
									gap: 8,
								}}
							>
								<div style={{ width: 18, borderTop: '2px dashed #2BA89866' }} />
								<span
									style={{
										fontFamily: 'DM Mono',
										fontSize: 9,
										color: '#4A6A66',
									}}
								>
									Greenwood
								</span>
							</div>
						</div>
					)}
				</div>
				<div style={S.sidebar}>
					<div
						style={{
							padding: '14px 16px 12px',
							borderBottom: '1px solid #1A2E2A',
							flexShrink: 0,
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 9,
								color: '#2BA898',
								letterSpacing: '.14em',
								textTransform: 'uppercase',
								marginBottom: 8,
							}}
						>
							{filter === 'all' ? 'All Locations' : `${filter} risk`} ·{' '}
							{filtered.length}
						</div>
						<div style={{ display: 'flex', gap: 20 }}>
							{[
								['Total', total],
								['Highest', max],
								[
									'Avg',
									streets.length ? (total / streets.length).toFixed(1) : '—',
								],
							].map(([l, v]) => (
								<div key={l}>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 20,
											color: '#2BA898',
											lineHeight: 1,
										}}
									>
										{v}
									</div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											color: '#2A4A46',
											textTransform: 'uppercase',
											letterSpacing: '.08em',
											marginTop: 3,
										}}
									>
										{l}
									</div>
								</div>
							))}
						</div>
					</div>
					<div className='cdm-sb' style={{ flex: 1, overflowY: 'auto' }}>
						{loading ? (
							<div
								style={{
									padding: 32,
									textAlign: 'center',
									fontFamily: 'DM Mono',
									fontSize: 10,
									color: '#2A4A46',
									letterSpacing: '.12em',
								}}
							>
								Loading…
							</div>
						) : (
							filtered.map((s, i) => {
								const level = riskLevel(s.count, max);
								const { fill } = riskColor(level);
								const barW = Math.max(4, Math.round((s.count / max) * 100));
								const isSel = selected?.street === s.street;
								return (
									<div
										key={s.street}
										className={`cdm-row${isSel ? ' sel' : ''}`}
										onClick={() => {
											setSelected(s);
											mapInstanceRef.current?.setView([s.lat, s.lng], 16, {
												animate: true,
											});
											if (isMobile) setListOpen(false);
										}}
									>
										<div
											style={{
												fontFamily: 'DM Mono',
												fontSize: 9,
												color: '#2A4A46',
												width: 18,
												flexShrink: 0,
											}}
										>
											{String(i + 1).padStart(2, '0')}
										</div>
										<div style={{ flex: 1, minWidth: 0 }}>
											<div
												style={{
													fontSize: isMobile ? 14 : 12,
													color: isSel ? 'white' : '#A0B0AE',
													lineHeight: 1.35,
													marginBottom: 5,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{s.street}
											</div>
											<div
												style={{
													height: 2,
													background: '#1A2E2A',
													borderRadius: 1,
													overflow: 'hidden',
												}}
											>
												<div
													style={{
														height: '100%',
														width: `${barW}%`,
														background: fill,
														borderRadius: 1,
													}}
												/>
											</div>
										</div>
										<div
											style={{
												textAlign: 'right',
												flexShrink: 0,
												marginLeft: 8,
											}}
										>
											<div
												style={{
													fontFamily: 'DM Mono',
													fontSize: isMobile ? 18 : 14,
													color: fill,
													fontWeight: 500,
												}}
											>
												{s.count}
											</div>
											<div
												style={{
													fontFamily: 'DM Mono',
													fontSize: isMobile ? 9 : 8,
													color: '#2A4A46',
													textTransform: 'uppercase',
													letterSpacing: '.08em',
													marginTop: 2,
												}}
											>
												{level}
											</div>
										</div>
									</div>
								);
							})
						)}
					</div>
					{isMobile && selected && (
						<div
							className='cdm-in'
							style={{
								borderTop: '1px solid #1A2E2A',
								padding: '14px 16px',
								background: '#0D1816',
								flexShrink: 0,
							}}
						>
							<div
								style={{
									fontFamily: 'DM Mono',
									fontSize: 9,
									color: '#2BA898',
									letterSpacing: '.12em',
									textTransform: 'uppercase',
									marginBottom: 6,
								}}
							>
								Selected
							</div>
							<div
								style={{
									fontFamily: 'Playfair Display',
									fontSize: 13,
									color: 'white',
									lineHeight: 1.4,
									marginBottom: 10,
								}}
							>
								{selected.street}
							</div>
							<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
								<div
									style={{
										fontFamily: 'DM Mono',
										fontSize: 24,
										color: '#2BA898',
									}}
								>
									{selected.count}
								</div>
								<span
									style={{
										fontFamily: 'DM Mono',
										fontSize: 10,
										textTransform: 'uppercase',
										letterSpacing: '.08em',
										color: riskColor(riskLevel(selected.count, max)).fill,
									}}
								>
									{riskLevel(selected.count, max)}
								</span>
								<button
									onClick={() => setSelected(null)}
									style={{
										marginLeft: 'auto',
										background: 'none',
										border: '1px solid #1A2E2A',
										color: '#4A6A66',
										padding: '4px 10px',
										cursor: 'pointer',
										fontFamily: 'DM Mono',
										fontSize: 9,
										textTransform: 'uppercase',
										letterSpacing: '.08em',
									}}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div
						style={{
							padding: '10px 16px',
							borderTop: '1px solid #1A2E2A',
							flexShrink: 0,
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 9,
								color: '#2A4A46',
								letterSpacing: '.1em',
								lineHeight: 1.7,
							}}
						>
							SOURCE · 1920 U.S. CENSUS RECORDS
							<br />
							CDM RESEARCH · PRELIMINARY DATA
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
